import { db } from "firebaseConfig";
import { useEffect, useState } from "react";
import {
  Skeleton,
  Stack,
  Box,
  FormControl,
  MenuItem,
  Select,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Timeline } from "@material-ui/icons";
import { TabContext, TabPanel } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import { ContactTableNestedRowSentMessages } from "./ContactTableNestedRowSentMessages";
import { ContactTableNestedRowGeneral } from "./ContactTableNestedRowGeneral";
import { ContactTableNestedRowTimeline } from "./ContactTableNestedRowTimeline";
import { ContactTableNestedRowSignatures } from "./ContactTableNestedRowSignatures";
import { getCountFromServer, query, collection, where } from "firebase/firestore";
import Label from "components/Label";
import CallsListPage from "pages/dashboard/CallsListPage/CallsListPage";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import { ListingMatchFavoriteListings } from "components/_dashboard/listingsMatch/ListingMatchFavoriteListings";
import { canShowListingMatches } from "utils/contacts/columns";
import { listDocuments } from "services/signatures";

import type {
  RealEstateContact,
  FavoriteListingsOfContact
} from "../../../../../@types/sharedSchema";
import type { RealEstateTab } from "../core/ContactTableTabs";

type ContactTableNestedRowTabsProps = {
  contact: RealEstateContact;
  currentTab: null | RealEstateTab;
  favoriteListingsOfContact: FavoriteListingsOfContact;
  handleEditContactDialogOpen: () => void;
  initialNestedTab?: NestedRowTabType;
  /**
   * Narrow-container layout: the dropdown tab picker and zero horizontal panel padding,
   * whatever the viewport is.
   *
   * DEFAULT OFF — every existing caller (dialog, chat card, navbar search, calendar,
   * calls, signatures, listing-match, rental-to-sale) is unaffected.
   *
   * It exists because the desktop/mobile switch below is `useMediaQuery`, i.e. a VIEWPORT
   * query, and the CRM side panel is a ~420px container on a 1440px desktop. Without this
   * the strip renders its 180px vertical sidebar next to the panels inside that 420px and
   * the content is squeezed to nothing. A container query would be the general fix; this
   * flag is the one line that does not require one.
   */
  compact?: boolean;
};

type TabsCount = {
  calls: number;
  sentMessages: number;
  signatures: number;
};

export function ContactTableNestedRowTabs({
  contact,
  currentTab,
  favoriteListingsOfContact,
  handleEditContactDialogOpen,
  initialNestedTab = "general",
  compact = false
}: ContactTableNestedRowTabsProps): JSX.Element {
  const [nestedCurrentTab, setNestedCurrentTab] = useState<NestedRowTabType>(initialNestedTab);

  const [tabsCount, setTabsCount] = useState<TabsCount | null>(null);
  const [loading, setLoading] = useState(true);

  const theme = useTheme();
  const isWideViewport = useMediaQuery(theme.breakpoints.up("md"));
  const isDesktop = isWideViewport && !compact;

  const {
    user: { email: userEmail }
  } = useAuth();

  const { userSettings } = useFirestore();

  // The document id IS the contact's phone (see `contactRekey.ts`). `callerPhoneNumber`
  // is a legacy mirror: absent on ~90% of documents and, where present, sometimes a
  // local-format string that agrees with nothing.
  const contactPhone = contact.id;

  const hasSignatureFeature = userSettings?.featureFlagsV2?.includes("signature") ?? false;
  // The user-doc snapshot rebuilds the virtualNumbers array identity on every
  // unrelated write; depending on the joined string keeps this effect from
  // refiring the whole count block (REST + N aggregate counts) each time.
  const virtualNumbersKey = (userSettings?.virtualNumbers ?? []).join(",");

  useEffect(() => {
    const virtualNumbers = virtualNumbersKey ? virtualNumbersKey.split(",") : [];

    const getContactTabsCount = async (): Promise<void> => {
      setLoading(true);

      const sentMessagesCountSnapshot = await getCountFromServer(
        query(
          collection(db, "dashboard-users-v2", userEmail, "sentMessages"),
          where("sentTo", "==", contactPhone)
        )
      );

      const sentMessagesCount = sentMessagesCountSnapshot.data().count;

      const callsCount = await getCallsCountFromUserPhoneNumber(virtualNumbers, contactPhone);

      // Signatures count comes from the backend REST endpoint rather than a
      // direct Firestore count to avoid depending on a composite index for
      // (ownerUserId, contactIds array-contains). The endpoint already
      // enforces ownership via auth middleware. Gated on the signature feature
      // flag like every other signature surface — without the gate this fired
      // a guaranteed 403 (logged server-side at ERROR) on every contact open
      // for every non-signature account.
      let signaturesCount = 0;
      if (hasSignatureFeature) {
        try {
          const documents = await listDocuments({ contactId: contactPhone });
          signaturesCount = documents.length;
        } catch {
          // Non-fatal — just show 0. Errors in the panel itself surface there.
        }
      }

      const tabsCount: TabsCount = {
        calls: callsCount,
        sentMessages: sentMessagesCount,
        signatures: signaturesCount
      };

      setTabsCount(tabsCount);

      setLoading(false);
    };

    void getContactTabsCount();
  }, [userEmail, contactPhone, virtualNumbersKey, hasSignatureFeature]);

  const { t: translate } = useTranslation();

  const changedNestedTabType = (newValue: NestedRowTabType) => (): void => {
    setNestedCurrentTab(newValue);
  };

  type NestedRowTab = {
    id: NestedRowTabType;
    count: number;
  };

  let nestedRowTabs: NestedRowTab[] = [
    {
      id: "general",
      count: 0
    },
    {
      id: "sentMessages",
      count: tabsCount?.sentMessages ?? 0
    },
    {
      id: "calls",
      count: tabsCount?.calls ?? 0
    },
    // Same feature-flag gate as the rest of the signature surfaces.
    ...(hasSignatureFeature
      ? [
          {
            id: "signatures" as const,
            count: tabsCount?.signatures ?? 0
          }
        ]
      : []),
    {
      id: "timeline",
      count: 0
    }
  ];

  const showListingMatchesTab = canShowListingMatches(currentTab);

  if (showListingMatchesTab) {
    nestedRowTabs = [
      ...nestedRowTabs,
      {
        id: "listingMatches",
        count: favoriteListingsOfContact?.listingIds.length ?? 0
      }
    ];
  }

  const renderTrailing = (id: NestedRowTabType, count: number): JSX.Element | null => {
    const showCount = id !== "general" && id !== "timeline";
    if (id === "timeline") return <Timeline fontSize="small" />;
    if (!showCount) return null;
    if (loading) return <Skeleton sx={{ borderRadius: 1 }} variant="circular" width={22} height={22} />;
    return <Label color="default">{count}</Label>;
  };

  // Custom sidebar instead of MUI's vertical TabList. MUI Tab at this
  // version (@material-ui/core 5.0.0-beta.4) hard-codes `flex-direction:
  // column` on the root and has internal styling quirks that make it
  // hard to get uniform row heights + proper inline alignment for
  // label/count, especially when counts vary in width (single-digit vs.
  // two-digit). A plain button list with flex layout gives predictable
  // sizing and a full-height indicator. TabContext/TabPanel still drive
  // panel visibility via nestedCurrentTab.
  const desktopTabList = (
    <Stack
      direction="column"
      role="tablist"
      aria-orientation="vertical"
      sx={{
        minWidth: 180,
        borderInlineEnd: 1,
        borderColor: "divider",
        // Pin to the top of the scroll container (DialogContent in the
        // modal variant). alignSelf: flex-start is required — parent is a
        // row Stack whose items default to stretch, which would make the
        // sidebar as tall as the panel and defeat the sticky.
        position: "sticky",
        top: 0,
        alignSelf: "flex-start"
      }}
    >
      {nestedRowTabs.map(({ id, count }) => {
        const isActive = nestedCurrentTab === id;
        return (
          <Box
            key={id}
            component="button"
            role="tab"
            aria-selected={isActive}
            onClick={changedNestedTabType(id)}
            sx={{
              // Reset native button styling
              background: "none",
              border: 0,
              font: "inherit",
              cursor: "pointer",
              // Layout — uniform 48px row, label start / count end,
              // vertically centered
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              minHeight: 48,
              px: 2,
              py: 1,
              position: "relative",
              textAlign: "start",
              color: isActive ? "primary.main" : "text.secondary",
              fontWeight: isActive ? 600 : 400,
              fontSize: "0.875rem",
              "&:hover": { bgcolor: "action.hover" },
              // Active indicator on the OUTER edge of the sidebar
              // (inline-start in the reading direction). The inner edge
              // hosts the divider border — separating the two avoids
              // visual clash.
              ...(isActive && {
                "&::before": {
                  content: '""',
                  position: "absolute",
                  insetInlineStart: 0,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  backgroundColor: "primary.main"
                }
              })
            }}
          >
            <span>{translate(`contactsPage.nestedRowTabs.${id}`)}</span>
            {renderTrailing(id, count)}
          </Box>
        );
      })}
    </Stack>
  );

  const mobileTabSelect = (
    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
      <Select
        value={nestedCurrentTab}
        onChange={(e) => setNestedCurrentTab(e.target.value as NestedRowTabType)}
      >
        {nestedRowTabs.map(({ id, count }) => (
          <MenuItem value={id} key={id}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ width: "100%", justifyContent: "space-between" }}
            >
              <span>{translate(`contactsPage.nestedRowTabs.${id}`)}</span>
              {renderTrailing(id, count)}
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // Unchanged for every existing caller; `compact` drops the 24px gutters, which in a
  // ~420px side panel were eating a tenth of the usable width on each edge.
  const panelSx = compact
    ? { px: 0, py: 0 }
    : { px: { xs: 0, md: 3 }, py: { xs: 0, md: 3 } };

  const panels = (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <TabPanel value="general" sx={panelSx}>
        <ContactTableNestedRowGeneral
          contact={contact}
          handleEditContactDialogOpen={handleEditContactDialogOpen}
        />
      </TabPanel>
      <TabPanel value="sentMessages" sx={panelSx}>
        <ContactTableNestedRowSentMessages contactPhone={contactPhone} />
      </TabPanel>
      <TabPanel value="calls" sx={panelSx}>
        <CallsListPage fromPhoneNumber={contactPhone} />
      </TabPanel>
      <TabPanel value="signatures" sx={panelSx}>
        <ContactTableNestedRowSignatures contact={contact} />
      </TabPanel>
      <TabPanel value="timeline" sx={panelSx}>
        <ContactTableNestedRowTimeline phoneNumberInternational={contactPhone} />
      </TabPanel>
      {showListingMatchesTab && (
        <TabPanel value="listingMatches" sx={panelSx}>
          <ListingMatchFavoriteListings
            contactId={contactPhone}
            contactData={contact}
            favoriteListingsOfContact={favoriteListingsOfContact}
          />
        </TabPanel>
      )}
    </Box>
  );

  return (
    <TabContext value={nestedCurrentTab}>
      <Stack direction={isDesktop ? "row" : "column"} sx={{ width: "100%" }}>
        {isDesktop ? desktopTabList : mobileTabSelect}
        {panels}
      </Stack>
    </TabContext>
  );
}

const nestedTabType = [
  "general",
  "sentMessages",
  "calls",
  "signatures",
  "timeline",
  "listingMatches"
] as const;

export type NestedRowTabType = typeof nestedTabType[number];

async function getCallsCountFromUserPhoneNumber(
  virtualNumbers: string[],
  phoneNumber: string
): Promise<number> {
  if (!virtualNumbers.length) return 0;

  const callsCount = await Promise.all(
    virtualNumbers.map(async (virtualNumber) => {
      const callsCountQuerySnapshot = await getCountFromServer(
        query(
          collection(db, "payCall-settings", virtualNumber, "calls-summary"),
          where("callerNumberInternational", "==", phoneNumber)
        )
      );

      return callsCountQuerySnapshot.data().count;
    })
  );

  const totalCallsCount = callsCount.reduce((acc, curr) => acc + curr, 0);

  return totalCallsCount;
}
